
import { collection, query, where, getDocs, deleteDoc, doc, Firestore, DocumentReference } from "firebase/firestore";
import { getLocationVersionsRef } from "./locationVersion/baseVersionService";

// Delete ALL version records for a location
export const deleteAllLocationVersions = async (db: Firestore, locationId: string) => {
  try {
    console.log(`[VERSION DELETE] Starting deletion of version records for location ID: ${locationId}`);
    
    const versionsRef = getLocationVersionsRef(db);
    const locationRef = doc(db, "campLocations", locationId);
    
    console.log(`[VERSION DELETE] Created reference to location: ${locationRef.path}`);
    
    // First query: Try to find by locationRef field which is the correct field
    const locationRefQuery = query(
      versionsRef,
      where("locationRef", "==", locationRef)
    );
    
    console.log(`[VERSION DELETE] Executing query by locationRef for location ID: ${locationId}`);
    const locationRefSnapshot = await getDocs(locationRefQuery);
    console.log(`[VERSION DELETE] Found ${locationRefSnapshot.size} version records by locationRef for location ${locationId}`);
    
    // Second query: Also try to find by data.id field which is a fallback
    // This catches versions that might have the location ID in a different field
    const dataIdQuery = query(
      versionsRef,
      where("data.id", "==", locationId)
    );
    
    console.log(`[VERSION DELETE] Executing backup query by data.id for location ID: ${locationId}`);
    const dataIdSnapshot = await getDocs(dataIdQuery);
    console.log(`[VERSION DELETE] Found ${dataIdSnapshot.size} version records by data.id for location ${locationId}`);
    
    // Third query: Check for versionData.id field (another possible location)
    const versionDataIdQuery = query(
      versionsRef,
      where("versionData.id", "==", locationId)
    );
    
    console.log(`[VERSION DELETE] Executing third query by versionData.id for location ID: ${locationId}`);
    const versionDataIdSnapshot = await getDocs(versionDataIdQuery);
    console.log(`[VERSION DELETE] Found ${versionDataIdSnapshot.size} version records by versionData.id for location ${locationId}`);
    
    // Combine the results ensuring no duplicates
    const seenIds = new Set();
    const allDocs = [];
    
    locationRefSnapshot.forEach(doc => {
      seenIds.add(doc.id);
      allDocs.push(doc);
    });
    
    dataIdSnapshot.forEach(doc => {
      if (!seenIds.has(doc.id)) {
        seenIds.add(doc.id);
        allDocs.push(doc);
      }
    });
    
    versionDataIdSnapshot.forEach(doc => {
      if (!seenIds.has(doc.id)) {
        allDocs.push(doc);
      }
    });
    
    console.log(`[VERSION DELETE] Total unique version records to delete: ${allDocs.length}`);
    
    if (allDocs.length === 0) {
      console.log(`[VERSION DELETE] No version records found for location ${locationId} - nothing to delete`);
      return true;
    }
    
    // Track each version being deleted
    const versionIds = allDocs.map(d => d.id);
    console.log(`[VERSION DELETE] Version IDs to delete: ${versionIds.join(', ')}`);
    
    // Delete each version document
    const deletions = allDocs.map(d => {
      const versionId = d.id;
      console.log(`[VERSION DELETE] Deleting version ${versionId} for location ${locationId}`);
      return deleteDoc(doc(db, "campLocationVersions", versionId));
    });
    
    await Promise.all(deletions);
    
    // Verification step - check if any versions remain
    const verificationQuery = query(versionsRef, where("locationRef", "==", locationRef));
    const verificationSnapshot = await getDocs(verificationQuery);
    
    if (!verificationSnapshot.empty) {
      console.error(`[VERSION DELETE] VERIFICATION FAILED: ${verificationSnapshot.size} versions still exist for location ${locationId}`);
      const remainingIds = verificationSnapshot.docs.map(d => d.id).join(', ');
      console.error(`[VERSION DELETE] Remaining version IDs: ${remainingIds}`);
    } else {
      console.log(`[VERSION DELETE] VERIFICATION PASSED: Successfully deleted all version records for location ${locationId}`);
    }
    
    console.log(`[VERSION DELETE] Successfully deleted ${deletions.length} version records for location ${locationId}`);
    return true;
  } catch (error) {
    console.error(`[VERSION DELETE] Failed to delete version records for location ${locationId}:`, error);
    throw error;
  }
};
